<template>
  <div class="sv-tabs" :class="tabsClasses" :style="{ color: hexColor }">
    <div
      v-if="backgroundWidth"
      class="sv-tab-background"
      :class="tabBackgroundClasses"
      :style="tabBackgroundStyles"
    />
    <div class="sv-tab-items">
      <div
        v-for="(mode, index) of itemsComputed"
        :id="_uid + '-sv-tab__' + index"
        :key="mode.value"
        class="sv-tab-item"
        :class="{
          'sv-tab-item__active': selectedIndex === index,
          'sv-tab-item__disabled': mode.disabled
        }"
        @click="onTabItemClick(index)"
      >
        <v-badge dot color="error" :value="mode.badge">
          <div
            class="sv-tab-item-content d-flex align-center"
            style="column-gap: 4px"
          >
            <v-icon v-if="mode.icon" size="16">
              {{ mode.icon }}
            </v-icon>
            <div v-if="mode.name" class="sv-tab-item-text">
              {{ mode.name }}
            </div>
          </div>
        </v-badge>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ToggleButton",
  props: {
    /**
     * Индекс элемента, если был передан массив строк в items, иначе - значение по ключу itemValue
     */
    value: {
      type: [Number, String],
      default: null
    },
    /**
     * Варианты. Может передаваться массив строк, тогда значением будет выступать индекс,
     * при этом может передаваться массив объектов. Во втором случае текст, значение, иконки, состояние disabled
     * и состояние badge регулируются соответствующими свойствами
     */
    items: {
      type: Array,
      default: () => []
    },
    /**
     * Ключ значения элемента (то, что будет возвращаться в value)
     */
    itemValue: {
      type: String,
      default: "id"
    },
    /**
     * Ключ текста элемента (то, что будет выводиться на кнопке)
     */
    itemText: {
      type: String,
      default: "name"
    },
    /**
     * Ключ иконки элемента
     */
    itemIcon: {
      type: String,
      default: "icon"
    },
    /**
     * Ключ флага отключенного элемента (если true, то будет disabled)
     */
    itemDisabled: {
      type: String,
      default: "disabled"
    },
    /**
     * Ключ флага badge элемента (если true, то будет badge)
     */
    itemBadge: {
      type: String,
      default: "badge"
    },
    /**
     * Включить режим большой кнопки
     */
    big: {
      type: Boolean,
      default: false
    },
    /**
     * Основной цвет
     */
    color: {
      type: String,
      default: "border-secondary"
    }
  },
  data() {
    return {
      valueInnerValue: this.value,
      backgroundWidth: "0px",
      backgroundX: "0px",
      backgroundTransition: false
    };
  },
  computed: {
    valueInner: {
      get() {
        return this.valueInnerValue;
      },
      set(value) {
        if (value === this.valueInnerValue) {
          return;
        }

        this.valueInnerValue = value;
        this.$emit("input", value);
      }
    },
    selectedIndex() {
      return this.itemsComputed.findIndex(
        mode => mode.value === this.valueInner
      );
    },
    tabsClasses() {
      return {
        "sv-tabs__big": this.big
      };
    },
    tabBackgroundClasses() {
      return {
        "sv-tab-background__left": this.selectedIndex === 0,
        "sv-tab-background__right":
          this.selectedIndex === this.itemsComputed.length - 1,
        "sv-tab-background__dark": this.$vuetify.theme.dark,
        "sv-tab-background__instant": !this.backgroundTransition
      };
    },
    tabBackgroundStyles() {
      return {
        left: this.backgroundX,
        width: this.backgroundWidth
      };
    },
    itemsComputed() {
      return this.items.map((mode, index) => {
        if (typeof mode === "object") {
          return {
            value: mode[this.itemValue],
            name: mode[this.itemText],
            icon: mode[this.itemIcon],
            disabled: !!mode[this.itemDisabled],
            badge: !!mode[this.itemBadge]
          };
        }

        return {
          value: index,
          name: mode,
          icon: "",
          disabled: false,
          badge: false
        };
      });
    },
    hexColor() {
      return this.$vuetify.theme.currentTheme[this.color] ?? this.color;
    }
  },
  watch: {
    value(val) {
      this.valueInnerValue = val;
    },
    valueInner() {
      this.backgroundTransition = true;
      this.calculateBackgroundPosition();
    }
  },
  mounted() {
    if (this.itemsComputed.length) {
      let index = this.selectedIndex;

      if (index === -1) {
        this.valueInner = this.itemsComputed[0].value;
      }

      document.fonts.ready.then(this.calculateBackgroundPosition);
    }
  },
  methods: {
    onTabItemClick(index) {
      if (this.itemsComputed[index].disabled) {
        return;
      }

      this.valueInner = this.itemsComputed[index].value;
    },
    calculateBackgroundPosition() {
      const tabElement = document.getElementById(
        this._uid + "-sv-tab__" + this.selectedIndex
      );

      let width = getComputedStyle(tabElement).width;
      width = Number(width.substring(0, width.length - 2));

      this.backgroundWidth = width + "px";
      this.backgroundX = 1 + this.selectedIndex;

      for (let i = 0; i < this.selectedIndex; i++) {
        const width = getComputedStyle(
          document.getElementById(this._uid + "-sv-tab__" + i)
        ).width;

        this.backgroundX += Number(width.substring(0, width.length - 2));
      }

      this.backgroundX += "px";
    }
  },
  hexColor() {
    return this.$vuetify.theme.currentTheme[this.color] ?? this.color;
  }
};
</script>

<style lang="scss">
$sv-toggle-button-border-radius: 4px;

.sv-tabs {
  display: inline-block;
  user-select: none;
  position: relative;

  .sv-tab-background {
    position: absolute;
    height: 100%;
    z-index: 1;
    transition: 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
    mix-blend-mode: darken;

    &__dark {
      mix-blend-mode: lighten;
    }

    &__left {
      border-bottom-left-radius: $sv-toggle-button-border-radius;
      border-top-left-radius: $sv-toggle-button-border-radius;
    }

    &__right {
      border-bottom-right-radius: $sv-toggle-button-border-radius;
      border-top-right-radius: $sv-toggle-button-border-radius;
    }

    &::before {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: inherit;

      display: block;
      content: "";

      background: currentColor;
      opacity: 0.4;
    }
  }

  .sv-tab-items {
    padding: 1px;

    display: flex;
    column-gap: 1px;
    background: currentColor;

    border-radius: 4px;

    .sv-tab-item {
      position: relative;
      height: 24px;
      padding: 0 16px;
      cursor: pointer;

      display: grid;
      place-items: center;
      background: var(--v-background-main-base);

      &:first-child {
        border-bottom-left-radius: $sv-toggle-button-border-radius;
        border-top-left-radius: $sv-toggle-button-border-radius;
      }

      &:last-child {
        border-bottom-right-radius: $sv-toggle-button-border-radius;
        border-top-right-radius: $sv-toggle-button-border-radius;
      }

      &::before {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: inherit;

        display: none;
        content: "";

        background: currentColor;
      }

      &:hover {
        &::before {
          display: block;
          opacity: 0.6;
        }
      }

      .sv-tab-item-content {
        color: var(--v-text-main-base);

        .v-icon {
          color: currentColor;
        }
      }

      .v-badge__badge {
        height: 6px !important;
        width: 6px !important;

        top: -2px !important;
        right: -6px !important;
        bottom: auto !important;
        left: auto !important;

        z-index: 1;
      }

      &__disabled {
        .sv-tab-item-content {
          color: var(--v-disabled-base);
        }
      }
    }
  }

  &__big {
    .sv-tab-items {
      .sv-tab-item {
        height: 38px;
      }
    }
  }
}
</style>
